/**
 * ErrorNotification Component - Foundational Primitive #13 (InfoBanner)
 *
 * Error/warning/info notification banners. Uses CSS primitive classes from:
 * - 09-info-banner.css for .info-banner styling
 *
 * Features:
 * - Sharp corners (border-radius: 0) per DO THIS/NOT THAT guidelines
 * - Status-appropriate colors (error/warning/info)
 * - Dismissible with close button
 * - Auto-stacking for multiple notifications
 */

import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useError, AppError } from '../../contexts/ErrorContext';
import { useTheme } from '../../contexts/ThemeContext';
import { setGlobalErrorHandler } from '../../services/api';
import '../../theme-components/09-info-banner.css';

interface ErrorNotificationProps {
  error: AppError;
}

const ErrorNotificationItem: React.FC<ErrorNotificationProps> = ({ error }) => {
  const { dismissError } = useError();
  const { theme } = useTheme();
  const { t } = useTranslation();

  const getErrorIcon = (type: string) => {
    switch (type) {
      case 'error': return '❌';
      case 'warning': return '⚠️';
      case 'info': return 'ℹ️';
      default: return '❌';
    }
  };

  const getErrorColors = (type: string) => {
    switch (type) {
      case 'error':
        return {
          bg: 'bg-red-50 border-red-200',
          text: 'text-red-800',
          button: 'text-red-600 hover:bg-red-100'
        };
      case 'warning':
        return {
          bg: 'bg-[var(--color-surface-warm)] border-[var(--color-gold)]',
          text: 'text-[var(--color-gold)]',
          button: 'text-[var(--color-gold)] hover:bg-[var(--color-surface-warm)]'
        };
      case 'info':
        return {
          bg: 'bg-gray-50 border-gray-300',
          text: 'text-amber-900',
          button: 'text-amber-700 hover:bg-gray-100'
        };
      default:
        return {
          bg: 'bg-red-50 border-red-200',
          text: 'text-red-800',
          button: 'text-red-600 hover:bg-red-100'
        };
    }
  };

  const colors = getErrorColors(error.type);

  return (
    <div
      className={`info-banner ${colors.bg} border sharp-corners p-4 mb-3 shadow-sm animate-in slide-in-from-top-2 duration-300`}
      role="alert"
      aria-live="assertive"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3 flex-1">
          <span className="text-lg flex-shrink-0" aria-hidden="true">
            {getErrorIcon(error.type)}
          </span>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-1">
              <p className={`${colors.text} font-semibold text-sm font-theme-primary`}>
                {error.message}
              </p>
              
              {error.code && error.code !== 'UNKNOWN_ERROR' && (
                <span className={`${colors.text} text-xs opacity-75 font-mono`}>
                  [{error.code}]
                </span>
              )}
            </div>
            
            {error.details && (
              <div className={`${colors.text} text-xs opacity-90 mt-1`}>
                <details className="cursor-pointer">
                  <summary className="hover:opacity-100">{t('common.errorNotification.showDetails')}</summary>
                  <div className="mt-2 p-2 bg-white bg-opacity-50 sharp-corners text-xs max-w-full overflow-hidden">
                    <pre className="whitespace-pre-wrap break-words overflow-x-auto max-h-32 scrollbar-thin">
                      {typeof error.details === 'string' 
                        ? error.details 
                        : JSON.stringify(error.details, null, 2)
                      }
                    </pre>
                  </div>
                </details>
              </div>
            )}
            
            <div className={`${colors.text} text-xs opacity-75 mt-1 flex items-center space-x-2`}>
              <span>{new Date(error.timestamp).toLocaleTimeString()}</span>
              {error.requestId && (
                <span className="font-mono">{t('common.errorNotification.idLabel', { id: error.requestId.slice(-8) })}</span>
              )}
            </div>
          </div>
        </div>
        
        <button
          onClick={() => dismissError(error.id)}
          className={`${colors.button} p-1 sharp-corners transition-colors duration-200 ml-2`}
          aria-label={t('common.errorNotification.dismissError')}
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path 
              fillRule="evenodd" 
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" 
              clipRule="evenodd" 
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export const ErrorNotificationContainer: React.FC = () => {
  const { errors, clearAllErrors, showError } = useError();
  const { t } = useTranslation();

  // Initialize global error handler once when component mounts
  useEffect(() => {
    const handleApiError = (error: any) => {
      showError({
        code: error.code || `HTTP_${error.status || 500}`,
        message: error.message || t('common.errorNotification.apiErrorOccurred'),
        status: error.status,
        details: error.details || error.response?.data,
        requestId: error.requestId,
        type: 'error'
      });
    };

    setGlobalErrorHandler(handleApiError);
  }, [showError, t]);

  if (errors.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-[9999] w-96 max-w-[calc(100vw-2rem)]">
      <div className="space-y-2">
        {errors.length > 1 && (
          <div className="flex justify-end mb-2">
            <button
              onClick={clearAllErrors}
              className="text-sm text-gray-600 hover:text-gray-800 bg-white bg-opacity-90 px-3 py-1 sharp-corners shadow-sm border border-gray-200 hover:bg-opacity-100 transition-colors duration-200 font-theme-primary"
            >
              {t('common.errorNotification.clearAll', { count: errors.length })}
            </button>
          </div>
        )}
        
        {errors.map(error => (
          <ErrorNotificationItem key={error.id} error={error} />
        ))}
      </div>
    </div>
  );
};

export default ErrorNotificationContainer;